import React, { useState, forwardRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ImageGenerationSection.css";
import { uploadPhoto } from "../../../config/uploadPhoto";
import { generateImagePrompt } from "../../../config/generateImagePrompt";
import { generateImageReplicate } from "../../../config/generateImageReplicate";
import { shareImage } from "../../../utils/shareUtils";
import { downloadImageFromUrl } from "../../../utils/downloadUtils";

import { StylizePhotoForPostcardApiSetting } from "../../../prompts/replicate/StylizePhotoForPostcardPrompt";
import { createPromptFluxKontextPro } from "../../../prompts/replicate/StylizePhotoForPostcardPrompt";
import {createCristmasPromt} from "../../../prompts/replicate/StylizePhotoForPostcardPrompt";
import { API_URLS } from "../../../config/api";
import SignUpModal from "../../Registration/SignUpModal/SignUpModal";

// ДОДАЄМО сюди функцію для збереження в галерею
async function saveImageToGallery(imageUrl) {
  try {
    const response = await fetch(API_URLS.ADD_TO_GALLERY, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ imageUrl }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Не вдалося додати зображення.");
    }

    console.log("✅ Зображення додано в галерею:", data.message);
  } catch (err) {
    console.error("❌ Помилка при додаванні в галерею:", err);
  }
}

const ImageGenerationSection = forwardRef(
  (
    {
      onImageGenerated,
      formData,
      onGenerateImageRef,
      greetingTextRef,
      generateImageData,
      onShowGreeting,
      hideBackgroundStep = false,
      useChristmasPrompt = false,
      useDirectPrompt = false,
    },
    ref
  ) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedImageUrl, setGeneratedImageUrl] = useState("");
    const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

    const navigate = useNavigate();

    // Перевіряємо статус авторизації
    useEffect(() => {
      const checkAuthStatus = () => {
        const token = localStorage.getItem('token');
        setIsUserLoggedIn(!!token);
      };
      
      checkAuthStatus();
      
      // Слухаємо зміни в localStorage
      const handleStorageChange = () => {
        checkAuthStatus();
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }, []);

    // Функції для управління модалкою реєстрації
    const handleOpenSignUpModal = () => {
      setIsSignUpModalOpen(true);
    };

    const handleCloseSignUpModal = () => {
      setIsSignUpModalOpen(false);
    };

    const handleSignUpSuccess = () => {
      setIsSignUpModalOpen(false);
      // Оновлюємо статус авторизації після успішної реєстрації
      setIsUserLoggedIn(true);
    };



    // Функція для переходу до редактора
    const handleEditImage = () => {
      if (!generatedImageUrl) return;

      // If parent provided an onShowGreeting handler, call it to reveal the greeting form
      if (typeof onShowGreeting === "function") {
        onShowGreeting();
        return;
      }

      // Fallback: navigate to editor with query params
      let textToUse = "";
      if (
        greetingTextRef &&
        greetingTextRef.current &&
        greetingTextRef.current.getCurrentText
      ) {
        textToUse = greetingTextRef.current.getCurrentText();
      } else {
        textToUse = formData.greetingText || "";
      }
      const params = new URLSearchParams({
        imageUrl: generatedImageUrl,
        text: textToUse,
      });
      navigate(`/editor?${params.toString()}`);
    };

    const generateImage = useCallback(async () => {
      // Перевіряємо заповненість форми
      if (!isFormComplete()) {
        alert('Please fill in the required fields: style, photo.');
        return;
      }

      setIsGenerating(true);

      try {
        console.log("FormData для генерації зображення:", formData);

        // Крок 1: Завантаження фото на Cloudinary (якщо є фото)
        let photoUrl =
          "https://res.cloudinary.com/dnma2ioeb/image/upload/v1754218865/pryvitai-photos/tldl1woyxzaqadwzogx1.jpg"; // заглушка
        if (formData.photo) {
          const convertToBase64 = (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result);
              reader.onerror = (error) => reject(error);
            });

          const photoBase64 = await convertToBase64(formData.photo);
          photoUrl = await uploadPhoto(photoBase64);
        }

        // Крок 2: Генерація або пряме використання промпта
        let finalPrompt;
        if (useDirectPrompt && useChristmasPrompt) {
          // Пряме використання промпта з createCristmasPromt
          finalPrompt = createCristmasPromt(formData);
        } else {
          // Звичайна генерація промпта
          const promptFunction = useChristmasPrompt ? createCristmasPromt : createPromptFluxKontextPro;
          const generatedImagePrompt = await generateImagePrompt(
            promptFunction(formData)
          );
          finalPrompt = generatedImagePrompt.generatedPrompt;
        }
        
        const generateImageData = StylizePhotoForPostcardApiSetting(
          formData,
          finalPrompt,
          photoUrl
        );

        console.log("Дані для генерації зображення:", generateImageData);

     

        // Крок 3: Генерація зображення через Replicate
        const replicateResult = await generateImageReplicate({
          modelId: generateImageData.modelId,
          input: generateImageData.input
        });

        // Обробляємо нову структуру відповіді
        const generatedImageUrlFromReplicate = replicateResult.generatedImageUrl || replicateResult;

        // Крок 4: Завантаження згенерованого зображення на Cloudinary
        const uploadedGeneratedImageUrl = await uploadPhoto(
          generatedImageUrlFromReplicate
        );
        setGeneratedImageUrl(uploadedGeneratedImageUrl);

        // Крок 5: Збереження в галереї користувача
        await saveImageToGallery(uploadedGeneratedImageUrl);

        if (onImageGenerated) {
          onImageGenerated("finalGeneratedImageUrl", uploadedGeneratedImageUrl);
        }
      } catch (error) {
        console.error("Помилка генерації зображення:", error);
        
        alert(error.message || 'Виникла помилка при генерації зображення. Спробуйте ще раз.');
      } finally {
        setIsGenerating(false);
      }
    }, [formData, onImageGenerated]);

    const isFormComplete = () => {
      let completedFields = 0;

      if (formData.cardStyle) completedFields++;
      if (formData.photo) completedFields++;
      if (formData.trait) completedFields++;

      return completedFields >= 2;
    };

    // Передаємо функцію generateImage через ref
    useEffect(() => {
      if (onGenerateImageRef) {
        onGenerateImageRef.current = { generateImage, isGenerating };
      }
    }, [generateImage, isGenerating, onGenerateImageRef]);

    // Функція для скачування зображення
    const handleDownloadImage = async () => {
      if (!generatedImageUrl) return;

      const filename = `pryvitai-${Date.now()}.png`;
      await downloadImageFromUrl(generatedImageUrl, filename);
    };

    // Функція для поділитися зображенням
    const handleShareImage = async () => {
      if (!generatedImageUrl) return;
      
      await shareImage({ url: generatedImageUrl }, 'Привітайка від Pryvitai');
    };



    return (
      <section ref={ref} className="IGS-image-generation-section">
      

        <div className="IGS-generation-controls">
          <button
            onClick={generateImage}
            disabled={isGenerating}
            className={`IGS-generate-image-button ${isGenerating ? "IGS-disabled" : ""}`}
          >
            {isGenerating ? (
              <>
                <span className="IGS-loading-spinner"></span>
                I’m creating a greeting card
              </>
            ) : generatedImageUrl ? (
              "Create again"
            ) : (
              "Create a greeting card"
            )}
          </button>
          
      
        </div>        {isGenerating && (
          <div className="IGS-generation-time-info">
            <p>Creating the greeting card takes about 2–3 minutes.</p>
          </div>
        )}

        {generatedImageUrl && (
          <div className="IGS-final-image-result">
            <p>
              <strong>🖼️ Final generated image:</strong>
            </p>

            <div className="IGS-image-preview">
              <img
                src={generatedImageUrl}
                alt="Generated image"
                className="IGS-preview-image"
              />
            </div>
            <p>🌟 The final image has been successfully created!</p>

            <div className="IGS-action-buttons">
              {!isUserLoggedIn ? (
                <button 
                  className="IGS-action-button IGS-edit-btn"
                  onClick={handleOpenSignUpModal}
                >
                  Signing Up to save your card
                </button>
              ) : (
                <>
                  <button onClick={handleDownloadImage} className="IGS-action-button IGS-download-btn">
                    <span className="IGS-button-icon">💾</span>
                    Save greeting card
                  </button> 
                  <button onClick={handleShareImage} className="IGS-action-button IGS-share-btn">
                    <span className="IGS-button-icon">📤</span>
                    Share
                  </button>
                </>
              )}

              {/* <button className="IGS-action-button IGS-edit-btn" onClick={handleEditImage}>
                <span className="IGS-button-icon">✏️</span>
                Add greeting text
              </button> */}

              
            </div>
          </div>
        )}
        
        {/* Модалка для реєстрації */}
        <SignUpModal
          isOpen={isSignUpModalOpen}
          onClose={handleCloseSignUpModal}
          onSuccess={handleSignUpSuccess}
        />
      </section>
    );
  }
);

export default ImageGenerationSection;
